import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Search, ChevronRight, Loader2 } from "lucide-react";

const SITE = "https://spa-report-hub.emergent.host";

const SEVERITY_STYLES = {
  critical: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Critique" },
  warning: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", label: "Attention" },
  info: { color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", label: "Info" },
};

export default function ErrorCodes() {
  const [facets, setFacets] = useState({ brands: [], categories: [] });
  const [brand, setBrand] = useState(null);
  const [q, setQ] = useState("");
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  useSEO({
    title: "Codes erreurs spa & jacuzzi — Balboa, Gecko, Watkins, Jacuzzi — DoctoSpa",
    description:
      "Retrouvez tous les codes erreurs de spa (FLO, OH, HL, SN, COM, PR, ICE, HH…) par plateforme : Balboa, Gecko, Watkins/HotSpring, Sundance, Jacuzzi, Wellis, Be Well. Signification, sévérité et actions correctives.",
    url: `${SITE}/codes-erreurs`,
  });

  useEffect(() => {
    api.get("/error-codes/facets").then((r) => setFacets(r.data || { brands: [], categories: [] }));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (brand) params.brand = brand;
    if (q.trim()) params.q = q.trim();
    api
      .get("/error-codes", { params })
      .then((r) => setCodes(r.data.items || []))
      .finally(() => setLoading(false));
  }, [brand, q]);

  const total = codes.length;

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14 lg:py-20" data-testid="error-codes-page">
      <div className="max-w-3xl">
        <div className="eyebrow">Codes erreurs spa</div>
        <h1 className="mt-3 font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
          Que signifie<br />
          <span className="text-primary">le code erreur</span> de votre spa&nbsp;?
        </h1>
        <p className="mt-5 text-muted-foreground leading-relaxed">
          Base de connaissances complète des codes affichés par les cartes électroniques
          Balboa, Gecko, Watkins, Sundance, Jacuzzi… Sévérité et action corrective en 1 clic.
        </p>
      </div>

      {/* Search bar */}
      <div className="mt-10">
        <div className="relative max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher : FLO, surchauffe, sonde, HL, OH…"
            className="pl-9 h-11"
            data-testid="error-codes-search"
          />
        </div>
      </div>

      {/* Brand facets */}
      <div className="mt-8" data-testid="error-codes-brand-facets">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Filtrer par plateforme</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => setBrand(null)}
            className={`text-sm font-semibold px-4 py-2 rounded-full transition-all ${
              brand === null ? "bg-secondary text-white shadow-md" : "bg-black/5 hover:bg-black/10 text-foreground"
            }`}
            data-testid="brand-facet-all"
          >
            Toutes <span className="opacity-60 text-xs">· {facets.brands.reduce((a, b) => a + b.count, 0)}</span>
          </button>
          {facets.brands.map((b) => (
            <button
              key={b.value}
              onClick={() => setBrand(b.value)}
              className={`text-sm font-semibold px-4 py-2 rounded-full transition-all ${
                brand === b.value ? "bg-secondary text-white shadow-md" : "bg-black/5 hover:bg-black/10 text-foreground"
              }`}
              data-testid={`brand-facet-${b.value}`}
            >
              {b.value} <span className="opacity-60 text-xs">· {b.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="mt-10">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {loading ? "Recherche…" : `${total} résultat${total > 1 ? "s" : ""}`}
          </div>
        </div>

        {loading ? (
          <div className="mt-6 flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : total === 0 ? (
          <p className="mt-6 text-sm text-muted-foreground">Aucun code trouvé. Essayez un autre filtre.</p>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="error-codes-grid">
            {codes.map((c) => {
              const s = SEVERITY_STYLES[c.severity] || SEVERITY_STYLES.warning;
              return (
                <Link
                  key={c.id}
                  to={`/codes-erreurs/${c.brand}/${c.code.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group"
                  data-testid={`error-code-item-${c.brand}-${c.code.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Card className={`p-4 border ${s.border} ${s.bg} hover:shadow-md transition-all h-full cursor-pointer`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-mono font-black text-lg ${s.color}`}>{c.code}</span>
                          <Badge variant="outline" className={`text-[10px] rounded-full ${s.color}`}>{s.label}</Badge>
                          <Badge variant="outline" className="text-[10px] rounded-full">{c.brand}</Badge>
                        </div>
                        <div className="mt-1.5 font-semibold text-sm">{c.title}</div>
                        <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{c.action_summary}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-60 shrink-0 mt-1 transition-opacity" />
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Section info */}
      <div className="mt-16 rounded-2xl bg-secondary text-white p-8 lg:p-10">
        <AlertTriangle className="h-8 w-8 text-primary" />
        <h3 className="mt-4 font-display text-2xl lg:text-3xl font-bold tracking-tight">
          Comment lire un code erreur ?
        </h3>
        <p className="mt-3 text-sm text-white/70 max-w-2xl leading-relaxed">
          Chaque marque affiche ses codes différemment. La plupart des spas modernes
          utilisent des cartes <b className="text-white">Balboa</b> ou <b className="text-white">Gecko</b> — la marque du contrôleur
          détermine le vocabulaire (FLO, OH, HL vs HH, SNA, SESC…). En cliquant sur un code,
          vous obtenez la signification, la sévérité et la marche à suivre.
        </p>
      </div>
    </div>
  );
}
